import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { COMMENT_FORM_URL } from '../links'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/heritage', label: 'Why This Matters' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/rfcp', label: 'Concept' },
    { path: '/faq', label: 'FAQ' },
    { path: COMMENT_FORM_URL, label: 'Comment', external: true }
  ]

  const isActive = (path: string) => location.pathname === path

  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  return (
    <header className="bg-gradient-to-b from-[#222426] to-[#434448] text-white shadow-lg sticky top-0 z-50 pt-[env(safe-area-inset-top)]">
      <div className="bg-black w-full">
        <div className="container-custom px-4 sm:px-6 lg:px-8 py-2">
          <div className="font-sora text-[13px] sm:text-[14px] font-normal leading-[18px] flex flex-wrap items-center gap-x-4 gap-y-1">
            <a
              href="https://maps.app.goo.gl/Tcc814yPstX1kj7a6"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline inline-flex items-center gap-1.5 min-h-11 sm:min-h-0"
            >
              <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" />
              </svg>
              <span className="hidden sm:inline">2007 Franklin St, San Francisco, CA 94109</span>
              <span className="sm:hidden">Map</span>
            </a>
            <a href="tel:+14154413000" className="hover:underline inline-flex items-center gap-1.5 min-h-11 sm:min-h-0">
              <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 11.2 18.8 19.5 19.5 0 0 1 5.2 12.8 19.8 19.8 0 0 1 2.08 4.17 2 2 0 0 1 4.07 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.62 2.6a2 2 0 0 1-.45 2.11L8 9.67a16 16 0 0 0 6.33 6.33l1.24-1.24a2 2 0 0 1 2.11-.45c.83.29 1.7.5 2.6.62A2 2 0 0 1 22 16.92z" />
              </svg>
              415-441-3000
            </a>
            <a href="mailto:haight@sfheritage.org" className="hover:underline inline-flex items-center gap-1.5 min-h-11 sm:min-h-0 break-all">
              <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>
              <span className="sm:hidden">Email</span>
              <span className="hidden sm:inline">haight@sfheritage.org</span>
            </a>
            <a
              href="http://www.sfheritage.org/about/give/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline inline-flex items-center gap-1.5 min-h-11 sm:min-h-0"
            >
              <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 21s-7-4.35-9.5-8.12C.64 10.15 1.2 6.7 3.9 5.05c2.06-1.25 4.56-.74 6.1.9L12 8l2-2.05c1.54-1.64 4.04-2.15 6.1-.9 2.7 1.65 3.26 5.1 1.4 7.83C19 16.65 12 21 12 21z" />
              </svg>
              Support<span className="hidden sm:inline"> SF Heritage</span>
            </a>
          </div>
        </div>
      </div>
      <div className="container-custom px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-32 gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center min-w-0 shrink">
            <img 
              src="/content/images/Logo_DHLA_Warp_White_1000.png" 
              alt="Doolan-Larson House & Storefronts" 
              className="h-10 md:h-24 w-auto max-h-full object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-5 lg:space-x-8" aria-label="Primary">
            {navItems.map((item) => (
              <div key={item.path}>
                {item.external ? (
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sora text-[14px] font-normal leading-[18px] text-white hover:underline underline-offset-4 decoration-2 transition-all duration-200"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    to={item.path}
                    className={`font-sora text-[14px] font-normal leading-[18px] text-white underline-offset-4 decoration-2 transition-all duration-200 ${
                      isActive(item.path) 
                        ? 'underline'
                        : 'hover:underline'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-3 -mr-2 text-white hover:text-haight-gold transition-colors duration-200 min-h-11 min-w-11 inline-flex items-center justify-center"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div id="mobile-nav" className="md:hidden pb-6 max-h-[calc(100dvh-8rem)] overflow-y-auto">
            <nav className="flex flex-col" aria-label="Mobile">
              {navItems.map((item) => (
                <div key={item.path}>
                  {item.external ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-sora text-[16px] font-normal leading-[18px] text-white hover:underline underline-offset-4 decoration-2 transition-all duration-200 flex items-center min-h-11 py-3"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className={`font-sora text-[16px] font-normal leading-[18px] text-white underline-offset-4 decoration-2 transition-all duration-200 flex items-center min-h-11 py-3 ${
                        isActive(item.path) 
                        ? 'underline'
                        : 'hover:underline'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
